import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../axios/api.js";

// ─── Leads list (LeadTable.jsx) ────────────────────────────────────────────
export const fetchLeads = createAsyncThunk(
  "leads/fetchLeads",
  async (market, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/clients", {
        params: { type: market },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch leads");
    }
  }
);

export const deleteLead = createAsyncThunk(
  "leads/deleteLead",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.delete(`/api/leads/delete/${id}`);
      if (response.status !== 200) {
        return rejectWithValue("Failed to delete the lead");
      }
      return id;
    } catch (error) {
      return rejectWithValue("Error occurred while deleting the lead");
    }
  }
);

export const fetchLeadsByCountryFilter = createAsyncThunk(
  "leads/fetchLeadsByCountryFilter",
  async ({ selectedLeads, archiveFilterActive }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/leadsCountryFilter", {
        selectedLeads,
        archiveFilterActive,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue("No data found for the selected filter");
    }
  }
);

export const downloadLeadsExcel = createAsyncThunk(
  "leads/downloadLeadsExcel",
  async (market, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/clients/download-excel", {
        params: { type: market },
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to download Excel file.");
    }
  }
);

// ─── Client / contact lookups (LeadForm.jsx, LeadModal.jsx) ───────────────
export const fetchClientNames = createAsyncThunk(
  "leads/fetchClientNames",
  async (market, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/clientsname", {
        params: { market },
      });
      return response.status === 200 ? response.data : [];
    } catch (error) {
      return rejectWithValue("Error fetching clients");
    }
  }
);

export const fetchClientContactDetails = createAsyncThunk(
  "leads/fetchClientContactDetails",
  async (clientName, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/client-contact-details", {
        params: [clientName],
      });
      return response.data?.[0] || null;
    } catch (error) {
      return rejectWithValue("Error fetching client address");
    }
  }
);

export const fetchContactDetails = createAsyncThunk(
  "leads/fetchContactDetails",
  async ({ contactPerson, clientName }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/contact-details", {
        params: [contactPerson, clientName],
      });
      return response.data?.[0] || null;
    } catch (error) {
      return rejectWithValue("Error fetching contact details");
    }
  }
);

export const fetchContactsCountry = createAsyncThunk(
  "leads/fetchContactsCountry",
  async (clientName, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/contacts-country", { clientName });
      return response.status === 200 ? response.data : [];
    } catch (error) {
      return rejectWithValue("Error fetching contacts country");
    }
  }
);

// ─── Create lead (LeadForm.jsx) ────────────────────────────────────────────
export const createLead = createAsyncThunk(
  "leads/createLead",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/leads", payload);
      if (response.status !== 200) {
        return rejectWithValue("Unexpected response status: " + response.status);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error creating lead");
    }
  }
);

// ─── Update lead (LeadUpdateForm.jsx) ──────────────────────────────────────
export const fetchLeadDetails = createAsyncThunk(
  "leads/fetchLeadDetails",
  async (rfq_no, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/inquiry-details", {
        params: { rfq_no },
      });
      if (response.data.length > 0) {
        return response.data[0];
      }
      return rejectWithValue("Lead not found");
    } catch (error) {
      return rejectWithValue("Failed to load lead data");
    }
  }
);

export const updateLead = createAsyncThunk(
  "leads/updateLead",
  async ({ rfq_no, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`/api/lead/${rfq_no}`, payload);
      if (response.data?.message === "Lead updated successfully and logged") {
        return response.data;
      }
      return rejectWithValue(response.data?.error || "Update failed. Please try again.");
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to update lead. Please try again."
      );
    }
  }
);

const leadSlice = createSlice({
  name: "leads",
  initialState: {
    leads: [],
    leadsLoading: false,
    error: null,

    deleting: false,

    filteredCountryLeads: [],
    filteringByCountry: false,

    downloadingExcel: false,

    clientNameOptions: [],
    clientNameOptionsLoading: false,

    clientContactDetails: null,
    contactDetails: null,

    countryOptions: [],
    countryOptionsLoading: false,

    creatingLead: false,

    selectedLead: null,
    selectedLeadLoading: false,

    updatingLead: false,
  },
  reducers: {
    clearSelectedLead: (state) => {
      state.selectedLead = null;
    },
    clearContactDetails: (state) => {
      state.contactDetails = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchLeads
      .addCase(fetchLeads.pending, (state) => {
        state.leadsLoading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.leadsLoading = false;
        state.leads = action.payload;
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.leadsLoading = false;
        state.error = action.payload;
      })

      // deleteLead
      .addCase(deleteLead.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deleteLead.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteLead.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload;
      })

      // fetchLeadsByCountryFilter
      .addCase(fetchLeadsByCountryFilter.pending, (state) => {
        state.filteringByCountry = true;
      })
      .addCase(fetchLeadsByCountryFilter.fulfilled, (state, action) => {
        state.filteringByCountry = false;
        state.filteredCountryLeads = action.payload;
      })
      .addCase(fetchLeadsByCountryFilter.rejected, (state, action) => {
        state.filteringByCountry = false;
        state.filteredCountryLeads = [];
        state.error = action.payload;
      })

      // downloadLeadsExcel
      .addCase(downloadLeadsExcel.pending, (state) => {
        state.downloadingExcel = true;
      })
      .addCase(downloadLeadsExcel.fulfilled, (state) => {
        state.downloadingExcel = false;
      })
      .addCase(downloadLeadsExcel.rejected, (state, action) => {
        state.downloadingExcel = false;
        state.error = action.payload;
      })

      // fetchClientNames
      .addCase(fetchClientNames.pending, (state) => {
        state.clientNameOptionsLoading = true;
      })
      .addCase(fetchClientNames.fulfilled, (state, action) => {
        state.clientNameOptionsLoading = false;
        state.clientNameOptions = action.payload;
      })
      .addCase(fetchClientNames.rejected, (state) => {
        state.clientNameOptionsLoading = false;
        state.clientNameOptions = [];
      })

      // fetchClientContactDetails
      .addCase(fetchClientContactDetails.fulfilled, (state, action) => {
        state.clientContactDetails = action.payload;
      })
      .addCase(fetchClientContactDetails.rejected, (state) => {
        state.clientContactDetails = null;
      })

      // fetchContactDetails
      .addCase(fetchContactDetails.fulfilled, (state, action) => {
        state.contactDetails = action.payload;
      })
      .addCase(fetchContactDetails.rejected, (state) => {
        state.contactDetails = null;
      })

      // fetchContactsCountry
      .addCase(fetchContactsCountry.pending, (state) => {
        state.countryOptionsLoading = true;
      })
      .addCase(fetchContactsCountry.fulfilled, (state, action) => {
        state.countryOptionsLoading = false;
        state.countryOptions = action.payload;
      })
      .addCase(fetchContactsCountry.rejected, (state) => {
        state.countryOptionsLoading = false;
        state.countryOptions = [];
      })

      // createLead
      .addCase(createLead.pending, (state) => {
        state.creatingLead = true;
        state.error = null;
      })
      .addCase(createLead.fulfilled, (state) => {
        state.creatingLead = false;
      })
      .addCase(createLead.rejected, (state, action) => {
        state.creatingLead = false;
        state.error = action.payload;
      })

      // fetchLeadDetails
      .addCase(fetchLeadDetails.pending, (state) => {
        state.selectedLeadLoading = true;
        state.error = null;
      })
      .addCase(fetchLeadDetails.fulfilled, (state, action) => {
        state.selectedLeadLoading = false;
        state.selectedLead = action.payload;
      })
      .addCase(fetchLeadDetails.rejected, (state, action) => {
        state.selectedLeadLoading = false;
        state.selectedLead = null;
        state.error = action.payload;
      })

      // updateLead
      .addCase(updateLead.pending, (state) => {
        state.updatingLead = true;
        state.error = null;
      })
      .addCase(updateLead.fulfilled, (state) => {
        state.updatingLead = false;
      })
      .addCase(updateLead.rejected, (state, action) => {
        state.updatingLead = false;
        state.error = action.payload;
      });
  },
});

export const { clearSelectedLead, clearContactDetails } = leadSlice.actions;
export default leadSlice.reducer;
